import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';
import Logo from '../components/Navbar/Logo';

const quickLinks = [
  { name: 'Home', href: '/' },
  { name: 'Services', href: '/services' },
  { name: 'Industries', href: '/industries' },
  { name: 'Pricing', href: '/pricing' },
  { name: 'About Us', href: '/about-us' },
  { name: 'FAQ', href: '/faq' },
  { name: 'Contact', href: '/contact' },
];

export default function Footer() {
  return (
    <footer className="bg-slate-900 text-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">

          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" aria-label="Home" className="flex items-center gap-3">
              <Logo />
              <span className="text-base font-bold text-white leading-tight">Cygnet Call Centre</span>
            </Link>
            <p className="mt-4 text-cygnet-light text-sm font-semibold italic">
              We listen. We care. We deliver.
            </p>
            <p className="mt-4 text-white/60 text-sm leading-7">
              UK-based call handling, virtual receptionist, and Duty of Care services. Trusted by UK businesses since 2000 — over 26 years of professional, 24/7 support.
            </p>
            {/* Certification badges */}
            <div className="mt-6 flex flex-wrap gap-3">
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2">
                <div className="w-5 h-5 rounded-full bg-green-400 flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-white">ISO 9001</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-3 py-2">
                <div className="w-5 h-5 rounded-full bg-blue-400 flex items-center justify-center shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-xs font-semibold text-white">ICO Registered</span>
              </div>
            </div>
          </div>

          {/* Quick links + Services — 2 columns on mobile */}
          <div className="col-span-1 md:col-span-1 lg:col-span-2 grid grid-cols-2 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">Quick Links</h3>
              <ul className="space-y-3">
                {quickLinks.map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-white/70 hover:text-white text-sm transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">Services</h3>
              <ul className="space-y-3">
                {[
                  { name: 'Call Handling', href: '/services#call-handling' },
                  { name: 'Virtual Receptionist', href: '/services#virtual-receptionist' },
                  { name: 'Email Monitoring', href: '/services#email-monitoring' },
                  { name: 'Duty of Care Calls', href: '/services#duty-of-care' },
                  { name: 'Appointment Reminders', href: '/services#appointment-reminders' },
                  { name: 'Emergency Line', href: '/services#emergency-line' },
                  { name: 'Order Taking', href: '/services#order-taking' },
                ].map((item) => (
                  <li key={item.name}>
                    <Link to={item.href} className="text-white/70 hover:text-white text-sm transition-colors">
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold text-white/40 uppercase tracking-wider mb-4">Contact</h3>
            <ul className="space-y-4">
              <li>
                <a href="tel:+442085385400" className="flex items-start gap-3 text-white/70 hover:text-white text-sm transition-colors">
                  <Phone className="w-4 h-4 shrink-0 mt-0.5 text-cygnet-light" />
                  0208 538 5400
                </a>
              </li>
              <li>
                <a href="mailto:info@cygnetcall.co.uk" className="flex items-start gap-3 text-white/70 hover:text-white text-sm transition-colors">
                  <Mail className="w-4 h-4 shrink-0 mt-0.5 text-cygnet-light" />
                  info@cygnetcall.co.uk
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-cygnet-light" />
                Suite 133, 981 Great West Road, Brentford, TW8 9DN
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-white/40">
          <p>&copy; {new Date().getFullYear()} Cygnet Call Centre Ltd. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
